import { useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { mutate } from 'swr';
import { authStorage, AUTH_TOKEN_STORAGE_KEY } from '@shared/api/client/auth-storage';
import { broadcastAuthLogout, subscribeToAuthLogout } from '@shared/api/client/auth-session-sync';
import { isSessionBoundToUser } from '@shared/api/client/session-identity';
import { createApiClient } from '@shared/api/client/api-client';
import { createAuthApi } from '@shared/api/lib/auth-api';
import { mapUserDtoToEntity } from '@entities/auth';
import { isFullAccessToken } from '@shared/lib/jwt';
import { ApiError, UnauthorizedError } from '@shared/api/errors/errors';
import {
  buildAuthReturnPath,
  resolveAuthReturnPath,
  type AuthReturnState,
} from '@shared/lib/auth-return-path';
import { AuthSessionProvider, type AuthLoginOptions, type AuthSession, type User } from '@entities/auth';

import { env } from '@shared/config/env';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const fetchUserGenerationRef = useRef(0);

  const isAuthenticated = !!user && authStorage.hasTokens();

  const fetchUser = async () => {
    const generation = ++fetchUserGenerationRef.current;

    try {
      const apiClient = createApiClient({ baseURL: env.API_BASE_URL });
      const authApi = createAuthApi(apiClient);
      const userDto = await authApi.getUser();
      const userData = mapUserDtoToEntity(userDto);

      if (generation !== fetchUserGenerationRef.current) {
        return userData;
      }

      setUser(userData);
      return userData;
    } catch (error) {
      if (generation === fetchUserGenerationRef.current) {
        console.error('Failed to fetch user:', error);
        if (error instanceof UnauthorizedError) {
          authStorage.clear();
          setUser(null);
        }
      }

      throw error;
    }
  };

  const login = async (
    token: string,
    userFromResponse?: User,
    options?: AuthLoginOptions,
  ): Promise<User | undefined> => {
    fetchUserGenerationRef.current += 1;
    authStorage.setAccessToken(token, { remember: options?.rememberMe });

    if (userFromResponse) {
      setUser(userFromResponse);
      return userFromResponse;
    }

    return fetchUser();
  };

  const performLocalLogout = useCallback(async () => {
    fetchUserGenerationRef.current += 1;
    authStorage.clear();
    await mutate(() => true, undefined, { revalidate: false });
    setUser(null);
    navigate('/auth', { replace: true });
  }, [navigate]);

  const logout = async () => {
    fetchUserGenerationRef.current += 1;

    try {
      const apiClient = createApiClient({ baseURL: env.API_BASE_URL });
      const authApi = createAuthApi(apiClient);
      await authApi.logout();
    } catch (error) {
      // В журнал не попадают токены, cookie и тело ответа сервера.
      console.error('Logout request failed', {
        method: 'POST',
        path: '/app/v1/auth/logout',
        status: error instanceof ApiError ? error.status : undefined,
      });
    } finally {
      broadcastAuthLogout();
      await performLocalLogout();
    }
  };

  const clearLocalSession = useCallback(async () => {
    fetchUserGenerationRef.current += 1;
    authStorage.clear();
    await mutate(() => true, undefined, { revalidate: false });
    setUser(null);
  }, []);

  const refreshUser = async () => {
    if (authStorage.hasTokens()) {
      try {
        await fetchUser();
      } catch (error) {
        console.error('Failed to refresh user:', error);
      }
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const accessToken = authStorage.getAccessToken();

      if (!accessToken) {
        setIsLoading(false);
        return;
      }

      if (!isFullAccessToken(accessToken)) {
        setIsLoading(false);
        return;
      }

      try {
        await fetchUser();
      } catch {
        // Token invalid — fetchUser already cleared storage when request was current.
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  useEffect(() => {
    return subscribeToAuthLogout(() => {
      void performLocalLogout();
    });
  }, [performLocalLogout]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || !user) {
        return;
      }

      if (!authStorage.hasTokens()) {
        void performLocalLogout();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [performLocalLogout, user]);

  // Вход другим аккаунтом в соседней вкладке: перезагрузка подтянет профиль актуального пользователя.
  useEffect(() => {
    if (!user) {
      return;
    }

    const reloadOnForeignSession = () => {
      if (!isSessionBoundToUser(user.id)) {
        window.location.reload();
      }
    };
    const handleStorage = (event: StorageEvent) => {
      if (event.key === AUTH_TOKEN_STORAGE_KEY && event.newValue != null) {
        reloadOnForeignSession();
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        reloadOnForeignSession();
      }
    };

    window.addEventListener('storage', handleStorage);
    window.addEventListener('focus', reloadOnForeignSession);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('focus', reloadOnForeignSession);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user]);

  // Redirect logic after loading
  useEffect(() => {
    if (isLoading) return;

    const accessToken = authStorage.getAccessToken();
    const hasValidSession =
      authStorage.hasTokens() && accessToken != null && isFullAccessToken(accessToken);

    if (hasValidSession && !user) return;

    const path = location.pathname;
    const isAuthPage =
      path === '/auth' || path === '/login' || path === '/register';
    const isPublicPage =
      isAuthPage ||
      path === '/404' ||
      path.startsWith('/invoice/') ||
      path.startsWith('/verify-email/') ||
      path.startsWith('/forgot-password') ||
      path.startsWith('/reset-password');

    const returnPath = buildAuthReturnPath(location.pathname, location.search);

    if (!isAuthenticated && !isPublicPage) {
      navigate('/auth', { replace: true, state: { from: returnPath } satisfies AuthReturnState });
    } else if (isAuthenticated && isAuthPage) {
      const homePath = user?.isAdmin ? '/workspace' : '/balance';
      navigate(resolveAuthReturnPath(location.state, homePath), { replace: true });
    }
  }, [isAuthenticated, isLoading, location.pathname, location.search, location.state, navigate, user]);

  const value: AuthSession = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshUser,
    clearLocalSession,
  };

  return <AuthSessionProvider value={value}>{children}</AuthSessionProvider>;
}
